// Small API client: same origin, cookie session, the X-Jarvis header on every call.
export class ApiError extends Error { constructor(public status: number, msg: string) { super(msg); } }

type BusyEvent = { busy: number } | { done: string } | { error: string };
const listeners = new Set<(e: BusyEvent) => void>();
let busy = 0;
const emit = (e: BusyEvent) => listeners.forEach((f) => f(e));
/** The busy layer listens here. */
export function onBusy(f: (e: BusyEvent) => void) { listeners.add(f); return () => { listeners.delete(f); }; }

export async function api<T = any>(path: string, body?: unknown, opts: { quiet?: boolean; method?: 'PATCH' | 'DELETE' } = {}): Promise<T> {
  const action = (body !== undefined || !!opts.method) && !opts.quiet;
  if (action) emit({ busy: ++busy });
  try {
    const r = await call<T>(path, body, opts.method);
    if (action) emit({ done: 'Kész' });
    return r;
  } catch (e: any) {
    if (action) emit({ error: e?.message || 'Hiba történt.' });
    throw e;
  } finally {
    if (action) emit({ busy: --busy });
  }
}

async function call<T>(path: string, body?: unknown, method?: string): Promise<T> {
  const r = await fetch(path, {
    method: method ?? (body === undefined ? 'GET' : 'POST'),
    headers: { 'X-Jarvis': '1', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, j.error || `hiba (${r.status})`);
  return j as T;
}

/** A file sent as it is (the bank's CSV export), with the busy layer. */
export async function upload<T = any>(path: string, file: File): Promise<T> {
  emit({ busy: ++busy });
  try {
    const r = await fetch(path, { method: 'POST', headers: { 'X-Jarvis': '1', 'Content-Type': 'text/csv' }, body: file, credentials: 'same-origin' });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new ApiError(r.status, j.error || `hiba (${r.status})`);
    emit({ done: 'Kész' });
    return j as T;
  } catch (e: any) { emit({ error: e?.message || 'Hiba történt.' }); throw e; }
  finally { emit({ busy: --busy }); }
}

/** Live updates: calls back when data changed on the server. Reconnects by itself. */
export function onChange(fn: (tables: string[]) => void): () => void {
  let es: EventSource | null = null, closed = false;
  const open = () => {
    es = new EventSource('/api/stream');
    es.onmessage = (m) => { try { const d = JSON.parse(m.data); if (d.type === 'changed') fn(d.tables); } catch { /* ignore */ } };
    es.onerror = () => { es?.close(); if (!closed) setTimeout(open, 4000); };
  };
  open();
  return () => { closed = true; es?.close(); };
}
